import { useState } from "react";
import { Link } from "react-router-dom";
import { Mic, ShieldCheck, Activity, Server, FileText, ArrowRight, Radio, Lock, Play, ArrowDown, ArrowUp, Cloud, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const steps = [
  { n: "01", icon: Mic, title: "Pendant records every interaction", text: "Each employee wears a discreet EXOPILOT pendant that records customer interactions and streams them over BLE to its paired companion phone app." },
  { n: "02", icon: Server, title: "Phone app relays to your local server", text: "The app forwards recordings to your on-premise server, where an offline AI engine scores every interaction against your SOP — no audio ever leaves the building." },
  { n: "03", icon: Activity, title: "Only scores reach the cloud", text: "Numeric quality scores sync upward to your cloud dashboard. SOPs sync downward. Voice data stays local, always." },
];

const features = [
  { icon: FileText, title: "SOP-driven evaluation", text: "Upload one standard operating procedure per department. Every interaction is measured against it — greeting, adherence, tone, resolution." },
  { icon: Radio, title: "Device assignment by serial", text: "Assign pendants to staff with a serial number and app ID. The mapping syncs to your local server automatically." },
  { icon: Lock, title: "Privacy by architecture", text: "Voice never transits the cloud. The uplink carries scores only; the downlink carries SOPs only. Auditable by design." },
  { icon: ShieldCheck, title: "Per-employee progress", text: "Trend lines, compliance breakdowns and session histories for every team member, updated as the local server reports in." },
];

const trustChips = ["Voice never leaves your premises", "Score-only cloud uplink", "Encrypted SOP sync", "Strict tenant isolation"];
const industries = ["Luxury hotels", "Jewellery retail", "Private banking", "Fine dining", "Flagship stores", "Concierge desks"];

const dimensions = [
  { name: "Greeting & welcome", score: 92, desc: "Did the interaction open to your standard — salutation, name usage, warmth in the first thirty seconds." },
  { name: "SOP adherence", score: 87, desc: "Step-by-step compliance with the department procedure, from verification through to hand-off." },
  { name: "Tone & courtesy", score: 90, desc: "Politeness, patience and pace held across the full conversation — not just the first minute." },
  { name: "Resolution & closing", score: 84, desc: "Was the customer's need actually met, and was the interaction closed to your closing standard." },
];

const rollout = [
  { when: "Day 1 · Morning", what: "Create your console, configure departments and upload your SOP documents." },
  { when: "Day 1 · Afternoon", what: "Stand up the local server on-site and pair pendants through the companion phone app." },
  { when: "Day 2", what: "Assign devices to employees by serial number — mappings and SOPs sync down automatically." },
  { when: "Day 3 onwards", what: "Scores stream in after every shift. Coaching conversations start with evidence, not anecdotes." },
];

const testimonials = [
  { quote: "We finally coach with evidence instead of anecdotes. Front-desk scores rose nine points in one quarter — and our guests' conversations never left the property.", name: "Rukmini Varma", role: "General Manager · heritage palace hotel, Kochi" },
  { quote: "In fine jewellery, one poorly handled consultation can cost a three-generation relationship. EXOPILOT shows us exactly where conversations drift from our standard.", name: "Arjun Mehta", role: "Retail Director · fine jewellery chain, 40 showrooms" },
  { quote: "Legal signed off in a week. There is nothing to review in the cloud except numbers — that simplicity is the entire audit.", name: "Claire Dubois", role: "Head of Compliance · private banking group" },
];

const faqs = [
  { q: "Does any audio ever reach the cloud?", a: "No. The cloud exposes no endpoint capable of receiving audio. Recordings travel from the pendant over BLE to the companion phone app and terminate at your on-premise server. The uplink is validated to structured numeric scores only — there is no configuration in which voice data can be uploaded." },
  { q: "What do employees actually wear?", a: "A lightweight pendant with a built-in microphone and a full-shift battery. It pairs over Bluetooth Low Energy with a companion app on a standard phone, which relays recordings to your local server. Battery level and last-seen status are visible in your console." },
  { q: "What happens if our internet goes down?", a: "Scoring continues uninterrupted — the evaluation engine runs fully offline on your local server against the last synced SOPs. Scores queue locally and sync to your console once connectivity returns. Nothing on the service floor depends on the cloud." },
  { q: "How do we keep this fair and lawful for staff?", a: "EXOPILOT is built for criteria-based coaching, not covert surveillance. We require deploying organisations to inform employees, obtain any consent local law requires, and share the SOPs they are measured against. Every score traces back to a published, versioned standard." },
  { q: "Can we update SOPs after rollout?", a: "Yes. Upload a new version to the SOP library at any time; it syncs to your local server on the next cycle and subsequent sessions are scored against it. Each department carries one active, versioned SOP so there is never ambiguity about the standard in force." },
  { q: "How long does deployment take?", a: "Most single-site deployments are scoring live interactions within two days: console setup and SOP upload on day one, local server and pendant pairing the same day, device assignment on day two. Multi-site rollouts repeat the same pattern per property." },
];

export default function Landing() {
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);
  const setContactField = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const submitContact = (e) => {
    e.preventDefault();
    setSendingContact(true);
    toast.success("Message sent", { description: "We'll get back to you within one business day." });
    setContact({ name: "", email: "", message: "" });
    setSendingContact(false);
  };

  return (
    <div className="min-h-screen bg-[#f9f9f8] text-[#111]">
      <header className="fixed top-0 inset-x-0 z-50 bg-[#f9f9f8]/85 backdrop-blur-xl border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600">
            <a href="#how" className="hover:text-[#111] transition-colors">How it works</a>
            <a href="#platform" className="hover:text-[#111] transition-colors">Platform</a>
            <a href="#privacy" className="hover:text-[#111] transition-colors">Privacy</a>
            <a href="#contact" className="hover:text-[#111] transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-neutral-600 hover:text-[#111] transition-colors px-3 py-2">Sign in</Link>
            <Link to="/register" data-testid="nav-register-link" className="text-sm bg-[#111] text-white rounded-full px-5 py-2 hover:bg-neutral-800 transition-colors">Get started</Link>
          </div>
        </div>
      </header>

      <section className="relative pt-32 sm:pt-40 pb-24 px-6 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-[-160px] w-[620px] h-[620px] rounded-full aurora-a" aria-hidden="true" />
        <div className="pointer-events-none absolute top-72 left-[-220px] w-[540px] h-[540px] rounded-full aurora-b" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="font-mono-data text-[11px] sm:text-xs uppercase tracking-[0.2em] text-neutral-500 mb-6">Service quality intelligence</p>
            <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Measure the quality of every customer conversation.
              <span className="font-serif-accent italic text-neutral-500"> Without the audio ever leaving your premises.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-neutral-600 max-w-xl leading-relaxed">
              EXOPILOT scores your service teams against your own SOPs — for hotels, jewellery showrooms and any floor where service quality matters. On-premise processing, cloud visibility.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to="/register" data-testid="hero-cta" className="inline-flex items-center justify-center gap-2 bg-[#111] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-800 transition-colors whitespace-nowrap w-full sm:w-auto">
                Open your console <ArrowRight className="w-4 h-4 shrink-0" />
              </Link>
              <a href="#how" className="text-sm text-neutral-600 hover:text-[#111] transition-colors border-b border-neutral-300 pb-0.5 self-center">See the architecture</a>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="trust-chips">
              {trustChips.map((c) => (
                <span key={c} className="inline-flex items-center justify-center gap-1.5 text-center text-xs text-neutral-600 bg-white/70 backdrop-blur-xl border border-neutral-200 rounded-full px-3.5 py-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-neutral-500 shrink-0" /> {c}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative fade-up fade-up-2" data-testid="hero-preview-card">
            <div className="float-slow bg-white/85 backdrop-blur-xl border border-neutral-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between pb-4 border-b border-neutral-100">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Live floor view</p>
              </div>
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#111] text-white flex items-center justify-center text-xs font-medium">PN</div>
                  <div>
                    <p className="text-sm font-medium">Priya Nair</p>
                    <p className="text-[11px] text-neutral-400">Front Desk · EXO-P1001</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl font-light">91.4</p>
                  <p className="text-[10px] text-green-700 font-mono-data">▲ +2.3 this week</p>
                </div>
              </div>
              <div className="space-y-3 pt-1">
                {[["Greeting", 94], ["SOP adherence", 89], ["Tone & courtesy", 93], ["Resolution", 88]].map(([label, v], i) => (
                  <div key={label}>
                    <div className="flex items-center justify-between text-[11px] text-neutral-500 mb-1">
                      <span>{label}</span><span className="font-mono-data">{v}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#111] rounded-full score-bar" style={{ width: `${v}%`, animationDelay: `${0.3 + i * 0.15}s` }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="pt-4 mt-4 border-t border-neutral-100 text-[10px] text-neutral-400 font-mono-data">Scores computed on-premise · synced 2 min ago</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16">
        <span className="sm:hidden block text-center text-xs text-neutral-500 whitespace-nowrap mb-3">Built for floors where service is the product</span>
        <div className="max-w-6xl mx-auto border-y border-neutral-200 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8" data-testid="industries-marquee">
          <span className="hidden sm:inline text-xs text-neutral-500 shrink-0 whitespace-nowrap">Built for floors where service is the product</span>
          <div className="relative flex-1 overflow-hidden marquee-mask w-full sm:w-auto">
            <div className="flex w-max marquee-track">
              {[...industries, ...industries].map((it, i) => (
                <span key={`${it}-${i}`} className="text-xs uppercase tracking-[0.18em] text-neutral-400 whitespace-nowrap pr-16">{it}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto fade-up fade-up-1">
          <div className="relative rounded-lg overflow-hidden border border-neutral-200">
            <img src="https://images.unsplash.com/photo-1724230758718-406bab979e67?w=1600&q=80" alt="Hotel reception" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-8 left-8 right-8 flex flex-wrap gap-6 text-white">
              {[["94.2", "Average service score"], ["0", "Audio files in the cloud"], ["100%", "SOP coverage"]].map(([v, l]) => (
                <div key={l} className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg px-6 py-4">
                  <div className="font-heading text-3xl font-light">{v}</div>
                  <div className="text-xs text-white/80 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="px-6 py-28 bg-[#111] text-[#ededed]">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">How it works</p>
          <h2 className="font-heading font-light text-lg md:text-lg max-w-lg text-neutral-300">A closed loop between your floor, your local server and your console.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 rounded-lg overflow-hidden mt-14">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#161616] p-10">
                <div className="flex items-center justify-between mb-10">
                  <s.icon className="w-5 h-5 text-neutral-400" />
                  <span className="font-mono-data text-xs text-neutral-600">{s.n}</span>
                </div>
                <h3 className="font-heading text-lg font-medium mb-3">{s.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="relative rounded-lg overflow-hidden border border-neutral-800 mt-14" data-testid="how-it-works-video">
            <img src="https://images.unsplash.com/photo-1626863905121-3b0c0ed7b94c?w=1600&q=80" alt="Service professional at work" className="w-full h-[440px] object-cover opacity-60" />
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-5">
              <button
                data-testid="play-video-button"
                onClick={() => toast("The full EXOPILOT product film is on its way", { description: "Meanwhile, open your console and explore the live demo data." })}
                aria-label="Play the EXOPILOT product film"
                className="w-20 h-20 rounded-full bg-white text-[#111] flex items-center justify-center hover:scale-105 transition-transform shadow-lg">
                <Play className="w-7 h-7 ml-1" fill="currentColor" />
              </button>
              <div className="text-center">
                <p className="text-sm font-medium text-white">Watch how EXOPILOT works</p>
                <p className="text-xs text-neutral-400 mt-1 font-mono-data">Product film · 2 min</p>
              </div>
            </div>
            <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-[11px] text-white/90 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> From pendant to score — the full loop
            </div>
          </div>
        </div>
      </section>

      <section id="platform" className="px-6 py-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Platform</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600">Everything your quality team needs, nothing your compliance team fears.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {features.map((f) => (
              <div key={f.title} className="bg-white border border-neutral-200 rounded-lg p-8 hover:-translate-y-0.5 hover:shadow-lg hover:border-neutral-300 transition-[transform,box-shadow,border-color] duration-200">
                <span className="w-10 h-10 rounded-md bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-6">
                  <f.icon className="w-5 h-5 text-[#111]" />
                </span>
                <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="relative px-6 pb-28 overflow-hidden">
        <div className="pointer-events-none absolute top-10 right-[-200px] w-[500px] h-[500px] rounded-full aurora-c" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="bg-white border border-neutral-200 rounded-xl p-5 sm:p-7 shadow-sm sm:shadow-md" data-testid="privacy-diagram">
              <div className="flex items-center gap-2.5 sm:gap-3 bg-neutral-50 border border-neutral-200 rounded-lg px-3.5 sm:px-4 py-3 sm:py-3.5">
                <Cloud className="w-4 h-4 text-neutral-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium">EXOPILOT Cloud Console</p>
                  <p className="text-[11px] text-neutral-400 font-mono-data">Scores · SOPs · device registry</p>
                </div>
              </div>
              <div className="flex flex-row items-center justify-center gap-4 sm:gap-10 py-3 sm:py-4 text-[11px] font-mono-data">
                <span className="inline-flex items-center gap-1.5 text-neutral-500 whitespace-nowrap"><ArrowDown className="w-3.5 h-3.5 shrink-0" /> SOPs & mappings</span>
                <span className="inline-flex items-center gap-1.5 text-green-700 whitespace-nowrap"><ArrowUp className="w-3.5 h-3.5 shrink-0" /> Scores only</span>
              </div>
              <div className="border-t-2 border-dashed border-neutral-300 relative my-1">
                <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-white px-2 sm:px-3 text-[10px] uppercase tracking-[0.18em] text-neutral-400 whitespace-nowrap">Premises boundary</span>
              </div>
              <p className="text-center text-[11px] text-red-600 font-mono-data pt-3 pb-4">Voice data never crosses this line</p>
              <div className="flex items-center gap-2.5 sm:gap-3 bg-[#111] text-white rounded-lg px-3.5 sm:px-4 py-3 sm:py-3.5">
                <Server className="w-4 h-4 text-neutral-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Your Local Server</p>
                  <p className="text-[11px] text-neutral-400 font-mono-data">Offline AI scoring against your SOPs</p>
                </div>
              </div>
              <div className="flex items-center justify-center py-3 text-[11px] font-mono-data text-neutral-500 text-center">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ArrowUp className="w-3.5 h-3.5 shrink-0" /> Recordings via companion app</span>
              </div>
              <div className="flex items-center gap-2.5 sm:gap-3 bg-neutral-50 border border-neutral-200 rounded-lg px-3.5 sm:px-4 py-3 sm:py-3.5">
                <Smartphone className="w-4 h-4 text-neutral-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Pendant + Phone App</p>
                  <p className="text-[11px] text-neutral-400 font-mono-data">BLE pairing on the service floor</p>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7 md:pl-6">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Privacy model</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">Cloud sees numbers.<br />Only your building hears voices.</h2>
            <ul className="mt-8 space-y-4 text-sm text-neutral-600">
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800" /> SOP documents sync from console to local server over an authenticated, key-protected channel.</li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800" /> The local evaluation engine runs fully offline against your SOPs — no internet required to score.</li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800" /> Only structured score payloads travel back up. No transcripts, no recordings, no exceptions.</li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800" /> Every workspace is tenant-isolated; brute-force protection and hashed credentials guard the console.</li>
            </ul>
            <Link to="/privacy" data-testid="privacy-policy-link" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#111] border-b border-neutral-400 pb-0.5 hover:border-[#111] transition-colors">
              Read the full privacy policy <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-10">
              <img src="https://images.unsplash.com/photo-1760088348194-a5ac70a8aa9f?w=1000&q=80" alt="EXOPILOT wearable device" className="rounded-lg border border-neutral-200 object-cover w-full h-[240px]" />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200 rounded-lg overflow-hidden" data-testid="trust-stats">
          {[["0", "voice files ever stored in the cloud"], ["100%", "of scoring runs on your premises"], ["4", "SOP compliance dimensions measured"], ["<1 min", "from device assignment to local sync"]].map(([v, l]) => (
            <div key={l} className="bg-white p-8">
              <div className="font-heading text-4xl font-light">{v}</div>
              <p className="text-xs text-neutral-500 mt-2 leading-relaxed">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">What gets measured</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">Four dimensions.<br />One published standard.</h2>
            <p className="mt-5 text-sm text-neutral-500 leading-relaxed">Every session is scored against the same four dimensions, derived from your department's SOP. Employees see the same criteria their coaches see — nothing is hidden, nothing is arbitrary.</p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dimensions-grid">
            {dimensions.map((d, i) => (
              <div key={d.name} className="group bg-white border border-neutral-200 rounded-lg p-6 hover:border-[#111] transition-colors">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-medium">{d.name}</h3>
                  <span className="font-heading text-2xl font-light">{d.score}</span>
                </div>
                <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-[#111] rounded-full score-bar group-hover:bg-neutral-600 transition-colors" style={{ width: `${d.score}%`, animationDelay: `${i * 0.12}s` }} />
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mt-4">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Deployment</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600">From empty console to live scoring in two days.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12" data-testid="rollout-timeline">
            {rollout.map((r, i) => (
              <div key={r.when} className="relative pt-8">
                <div className="absolute top-0 left-0 right-0 h-px bg-neutral-200" />
                <div className="absolute top-[-4px] left-0 w-2 h-2 rounded-full bg-[#111]" />
                <p className="font-mono-data text-xs text-neutral-400">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="text-sm font-medium mt-2">{r.when}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed mt-2">{r.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-6 py-28 bg-[#111] text-[#ededed] overflow-hidden">
        <div className="pointer-events-none absolute -bottom-40 left-1/3 w-[560px] h-[560px] rounded-full aurora-b opacity-40" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">What operators say</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-300">Trusted where a single conversation carries the brand.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14" data-testid="testimonials-grid">
            {testimonials.map((t) => (
              <figure key={t.name} className="bg-[#161616] border border-neutral-800 rounded-lg p-8 flex flex-col justify-between hover:-translate-y-0.5 transition-transform duration-200">
                <blockquote className="font-serif-accent italic text-lg leading-relaxed text-neutral-200">“{t.quote}”</blockquote>
                <figcaption className="mt-8 pt-5 border-t border-neutral-800">
                  <p className="text-sm font-medium text-white">{t.name}</p>
                  <p className="text-xs text-neutral-500 mt-1">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Questions</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">Asked by every operations and compliance team.</h2>
            <p className="mt-5 text-sm text-neutral-500 leading-relaxed">Something more specific? <a href="mailto:privacy@exopilot.io" className="text-[#111] underline underline-offset-4">Write to us</a> or read the <Link to="/privacy" className="text-[#111] underline underline-offset-4">full privacy policy</Link>.</p>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-neutral-200">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-neutral-500 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto bg-[#111] text-white rounded-lg p-9 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-light text-3xl md:text-4xl tracking-tight">Bring measurable standards to your service floor.</h2>
            <p className="text-neutral-400 text-sm mt-3">Set up your console, add your team and sync your first SOP in minutes.</p>
          </div>
          <Link to="/register" data-testid="footer-cta" className="shrink-0 inline-flex items-center gap-2 bg-white text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-200 transition-colors">
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section id="contact" className="px-6 pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Get in touch</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">Talk to us about your floor.</h2>
            <p className="mt-5 text-sm text-neutral-500 leading-relaxed">Tell us about your properties, showrooms or branches and we'll help you scope a rollout. Prefer email? Write to <a href="mailto:hello@exopilot.io" className="text-[#111] underline underline-offset-4">hello@exopilot.io</a>.</p>
          </div>
          <div className="md:col-span-8">
            <form onSubmit={submitContact} data-testid="contact-form" className="bg-white border border-neutral-200 rounded-lg p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Name</label>
                  <input data-testid="contact-name-input" required value={contact.name} onChange={setContactField("name")} placeholder="Jane Doe"
                    className="mt-1.5 w-full bg-[#f9f9f8] border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Work email</label>
                  <input data-testid="contact-email-input" type="email" required value={contact.email} onChange={setContactField("email")} placeholder="you@company.com"
                    className="mt-1.5 w-full bg-[#f9f9f8] border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Message</label>
                <textarea data-testid="contact-message-input" required rows={4} value={contact.message} onChange={setContactField("message")} placeholder="Tell us about your floor, sites and timeline."
                  className="mt-1.5 w-full bg-[#f9f9f8] border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent resize-none" />
              </div>
              <button type="submit" data-testid="contact-submit-button" disabled={sendingContact}
                className="inline-flex items-center gap-2 bg-[#111] text-white rounded-full px-7 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-60">
                {sendingContact ? "Sending…" : "Send message"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 px-6 py-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo />
          <nav className="flex flex-wrap gap-4 sm:gap-6 text-sm text-neutral-500">
            <a href="#how" className="hover:text-[#111] transition-colors">How it works</a>
            <a href="#platform" className="hover:text-[#111] transition-colors">Platform</a>
            <a href="#contact" className="hover:text-[#111] transition-colors">Contact</a>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#111] transition-colors">Privacy Policy</Link>
          </nav>
          <p className="text-xs text-neutral-400">© 2026 EXOPILOT · Service quality intelligence · On-premise voice processing</p>
        </div>
      </footer>
    </div>
  );
}
